<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
// @ts-expect-error js file
import store from '@/store'
import { mdiMagnify, mdiPlus } from '@mdi/js'
import { Studio } from '@/modules/Studios/types'
import { StudioApi } from '@/enitites/studio'
import { useDebounceFn } from '@vueuse/core'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKSvgIcon from '@/components/AppShared/BKSvgIcon.vue'
import StudiosGridView from '@/modules/Studios/components/lists/StudiosGridView.vue'
import StudiosMapView from '@/modules/Studios/components/lists/StudiosMapView.vue'
import Bugsnag from '@bugsnag/js'

const router = useRouter()
const route = useRoute()

const studios = ref<Studio[]>([])
const currentUserStudios = ref<Studio[]>([])
const viewType = ref<'grid' | 'map'>('grid')

const getUserDB = computed(() => store.getters.getUserDB)
const createNewStudio = async () => {
  if (!getUserDB.value) {
    await router.push({
      name: 'Signup',
      query: { redirect: route.path },
    })
    return
  }

  const studioId = await StudioApi.createStudio()
  if (studioId) {
    router.push({ name: 'CreateStudio', params: { id: studioId.toString() } })
  }
}

const studioListPayload = ref({
  pageNumber: 0,
  pageSize: 100,
  keyword: '',
})

const fetchStudios = async () => {
  try {
    const response = await StudioApi.fetchStudiosList(studioListPayload.value)
    if (response && response.length) {
      studios.value = response
    }
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching studios',
        payload: studioListPayload.value,
      })
    })
    studios.value = []
  }
}

const fetchUserStudios = async () => {
  try {
    if (getUserDB.value && getUserDB.value.id) {
      const response = await StudioApi.fetchStudiosList({
        ...studioListPayload.value,
        userId: getUserDB.value.id,
      })
      if (response && response.length) {
        currentUserStudios.value = response
      }
    }
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching user studios',
        userId: getUserDB.value?.id,
      })
    })
    currentUserStudios.value = []
  }
}

const debounceFetchStudios = useDebounceFn(() => {
  studioListPayload.value.pageNumber = 0
  fetchStudios()

  // Load user studios if user is logged in
  if (getUserDB.value) {
    fetchUserStudios()
  }
}, 500)

onMounted(async () => {
  // Load all studios
  fetchStudios()

  // Load user studios if user is logged in
  if (getUserDB.value) {
    await fetchUserStudios()
  }
})

const setViewType = (type: 'grid' | 'map') => {
  viewType.value = type
}
</script>

<template>
  <section
    class="studios"
    :style="{ backgroundColor: viewType === 'grid' ? 'var(--color-dark-900)' : 'var(--color-white)' }"
  >
    <div class="page-header">
      <div class="title-container">
        <v-btn
          class="create-studio-button"
          color="var(--color-white)"
          icon
          @click="createNewStudio"
        >
          <v-icon class="icon-plus">
            {{ mdiPlus }}
          </v-icon>
        </v-btn>
        <h1 class="page-title">Studios</h1>
      </div>
      <BKInput
        v-model="studioListPayload.keyword"
        :prepend-icon="mdiMagnify"
        class="search"
        color="var(--color-grey-500)"
        hide-details
        placeholder="Search studios"
        @input="debounceFetchStudios"
      />
      <div class="views-wrapper">
        <v-btn
          depressed
          :text="viewType !== 'grid'"
          class="view-tab"
          @click="setViewType('grid')"
        >
          <BKSvgIcon
            class="tab-icon"
            color="var(--color-grey-300)"
            height="20px"
            name="grid"
            width="20px"
          />
          Grid
        </v-btn>
        <v-btn
          depressed
          :text="viewType !== 'map'"
          class="view-tab"
          @click="setViewType('map')"
        >
          <BKSvgIcon
            class="tab-icon"
            color="var(--color-grey-300)"
            height="20px"
            name="pinpoint"
            width="20px"
          />
          Map
        </v-btn>
      </div>
    </div>

    <div class="content-container">
      <StudiosGridView
        v-if="viewType === 'grid'"
        :studios="studios"
        :own-studios="currentUserStudios"
      />
      <StudiosMapView
        v-else-if="viewType === 'map'"
        :studios="studios"
        :keyword="studioListPayload.keyword"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.studios {
  background-color: var(--color-dark-900);

  .page-header {
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 16px;
    align-items: center;
    padding: calc(var(--header-height) + 40px) 50px 40px;
    overflow: hidden;
    background-color: var(--color-white);
    border-radius: 0 0 20px 20px;
    box-shadow: 0 2px 8px rgb(0 0 0 / 10%);

    @media (max-width: $screen-sm) {
      grid-template-columns: auto 1fr;
      padding: calc(var(--header-height) + 20px) 20px 20px;
    }

    @media (max-width: 500px) {
      grid-template-columns: 1fr;
    }
  }

  .title-container {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;
    color: var(--color-blue-800);

    .page-title {
      @include font-h2;

      text-transform: uppercase;

      @media (max-width: 768px) {
        @include font-h3;
      }
    }
  }

  .create-studio-button {
    width: 40px;
    height: 40px;
    background-color: var(--color-blue-800) !important;

    .icon-plus {
      width: 24px;

      @media (max-width: 768px) {
        width: 20px;
      }
    }

    @media (max-width: 768px) {
      width: 30px;
      height: 30px;
    }
  }

  .search {
    width: 100%;
    min-width: 335px;

    @media (max-width: $screen-md) {
      min-width: 100%;
    }
  }

  .views-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-end;

    @media (max-width: $screen-sm) {
      grid-column: span 2;
    }

    @media (max-width: 500px) {
      grid-column: span 1;
    }

    .view-tab {
      padding: 10px 26px;
      text-transform: none;
      border-radius: 6px;

      @media (max-width: $screen-sm) {
        flex: 1 0 0;
      }
    }

    .tab-icon {
      margin-right: 5px;
    }
  }
}
</style>
